import { localizer } from "@stormglass/game-localization";

import { useGameHost, useGameState } from "../game/GameContext";

export function SettingsScreen(): React.JSX.Element {
  const host = useGameHost();
  const { settings } = useGameState();
  return (
    <div className="settings-page page-panel">
      <p className="eyebrow">CHAMBER PREFERENCES</p>
      <h1>{localizer.t("settings.title")}</h1>
      <p className="page-lede">
        Tune presentation without changing simulation outcomes.
      </p>
      <div className="settings-groups">
        <section>
          <div className="settings-group__heading">
            <span>◉</span>
            <div>
              <h2>Display</h2>
              <p>Clarity, motion and device performance.</p>
            </div>
          </div>
          <label className="setting-row">
            <span>
              <strong>{localizer.t("settings.quality")}</strong>
              <small>Auto adapts particles and effects to this device.</small>
            </span>
            <select
              value={settings.quality}
              onChange={(event) => {
                host.updateSettings({
                  quality: event.target.value as typeof settings.quality,
                });
              }}
            >
              <option value="auto">Auto</option>
              <option value="high">High</option>
              <option value="balanced">Balanced</option>
              <option value="low">Low</option>
            </select>
          </label>
          <Toggle
            label={localizer.t("settings.motion")}
            detail="Removes camera movement and shortens atmospheric effects."
            checked={settings.reducedMotion}
            onChange={(value) => {
              host.updateSettings({ reducedMotion: value });
            }}
          />
          <Toggle
            label={localizer.t("settings.contrast")}
            detail="Strengthens outlines and non-color threat cues."
            checked={settings.highContrast}
            onChange={(value) => {
              host.updateSettings({ highContrast: value });
            }}
          />
          <label className="setting-row">
            <span>
              <strong>{localizer.t("settings.numbers")}</strong>
              <small>Control combat number density.</small>
            </span>
            <select
              value={settings.damageNumbers}
              onChange={(event) => {
                host.updateSettings({
                  damageNumbers: event.target
                    .value as typeof settings.damageNumbers,
                });
              }}
            >
              <option value="all">All</option>
              <option value="critical">Critical only</option>
              <option value="off">Off</option>
            </select>
          </label>
        </section>
        <section>
          <div className="settings-group__heading">
            <span>⌁</span>
            <div>
              <h2>Sound & touch</h2>
              <p>Independent atmospheric audio buses.</p>
            </div>
          </div>
          <Range
            label={localizer.t("settings.master")}
            value={settings.masterVolume}
            onChange={(value) => {
              host.updateSettings({ masterVolume: value });
            }}
          />
          <Range
            label={localizer.t("settings.music")}
            value={settings.musicVolume}
            onChange={(value) => {
              host.updateSettings({ musicVolume: value });
            }}
          />
          <Range
            label={localizer.t("settings.effects")}
            value={settings.effectsVolume}
            onChange={(value) => {
              host.updateSettings({ effectsVolume: value });
            }}
          />
          <Toggle
            label="Haptics"
            detail="Brief device feedback for breaches and boss phases."
            checked={settings.haptics}
            onChange={(value) => {
              host.updateSettings({ haptics: value });
            }}
          />
        </section>
        <section>
          <div className="settings-group__heading">
            <span>◇</span>
            <div>
              <h2>Privacy</h2>
              <p>Both optional systems are disabled by default.</p>
            </div>
          </div>
          <Toggle
            label={localizer.t("settings.analytics")}
            detail="Share anonymous run and performance events."
            checked={settings.analytics}
            onChange={(value) => {
              host.updateSettings({ analytics: value });
            }}
          />
          <Toggle
            label={localizer.t("settings.crashes")}
            detail="Share scrubbed technical faults; never save contents."
            checked={settings.crashReports}
            onChange={(value) => {
              host.updateSettings({ crashReports: value });
            }}
          />
          <button
            className="button button--ghost diagnostics-button"
            type="button"
            onClick={() => void host.exportDiagnostics()}
          >
            Export local diagnostics
          </button>
        </section>
      </div>
    </div>
  );
}

function Toggle({
  label,
  detail,
  checked,
  onChange,
}: {
  readonly label: string;
  readonly detail: string;
  readonly checked: boolean;
  readonly onChange: (value: boolean) => void;
}): React.JSX.Element {
  return (
    <label className="setting-row">
      <span>
        <strong>{label}</strong>
        <small>{detail}</small>
      </span>
      <input
        className="toggle"
        type="checkbox"
        checked={checked}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
      />
    </label>
  );
}

function Range({
  label,
  value,
  onChange,
}: {
  readonly label: string;
  readonly value: number;
  readonly onChange: (value: number) => void;
}): React.JSX.Element {
  return (
    <label className="setting-row setting-row--range">
      <span>
        <strong>{label}</strong>
        <small>{Math.round(value * 100)}%</small>
      </span>
      <input
        type="range"
        min="0"
        max="1"
        step="0.01"
        value={value}
        onChange={(event) => {
          onChange(event.target.valueAsNumber);
        }}
      />
    </label>
  );
}
